import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { connect, useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import { Base64 } from 'js-base64';
import { Sticky, StickyContainer } from 'react-sticky';
import { useUnmount } from 'react-use';
import type { Dispatch } from 'redux';

import { RootActions } from 'CorePackage/MainModule/types/RootActions';
import { Accordion } from 'UIPackage/MainModule/components/Accordion';
import { ROUTE_ANALYTICS_PROSECTOR_KNM } from 'CorePackage/MainModule/routes/routes';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { deleteObjectProperties } from 'CorePackage/MainModule/common/deleteObjectProperties';
import { IErknmAnalyticsIndex } from 'APIPackage/ErknmAnalyticsModule/interfaces/IErknmAnalyticsIndex';
import { IErknmAnalyticsFilter } from 'AnalyticsPackage/ErknmAnalyticsModule/interfaces/IErknmAnalyticsFilter';
import { ErknmAnalyticsColumnValueTypeEnum } from 'APIPackage/ErknmAnalyticsModule/enums/ErknmAnalyticsColumnValueTypeEnum';
import { IErknmAnalyticsTableColumn } from 'AnalyticsPackage/ErknmAnalyticsModule/interfaces/IErknmAnalyticsTableColumn';

import {
  getErknmAnalyticsProsecutorOrganizationData,
  getErknmAnalyticsProsecutorOrganizationFilter,
  getErknmAnalyticsProsecutorOrganizationTableColumns,
} from '../selectors/erknmAnalyticsProsecutorOrganizationSelectors';
import styles from './ErknmAnalyticsProsecutorOrganizationTable.module.scss';
import { ErknmAnalyticsProsecutorOrganizationColumnModal } from './ErknmAnalyticsProsecutorOrganizationColumnModal';
import {
  erknmAnalyticsProsecutorOrganizationGetDataNext,
  erknmAnalyticsProsecutorOrganizationSetTableColumns,
} from '../actions/erknmAnalyticsProsecutorOrganizationActions';
import { formatCurrency } from '../common/formatCurrency';

interface IErknmAnalyticsTable {
  data: IErknmAnalyticsIndex[];
  tableColumns: IErknmAnalyticsTableColumn[];
  filter: IErknmAnalyticsFilter | undefined;
}

const ErknmAnalyticsProsecutorOrganizationTableWithoutStore = ({
  data,
  tableColumns,
  filter,
}: IErknmAnalyticsTable) => {
  const dispatch = useDispatch<Dispatch<RootActions>>();
  const [currentData, setCurrentData] = useState<Array<IErknmAnalyticsIndex>>([]);
  const [modifiedColumn, setModifiedColumn] = useState<IErknmAnalyticsTableColumn | null>(null);
  const tableRef = useRef<HTMLTableElement>(null);
  const tableHeadRef = useRef<HTMLTableElement>(null);
  const [isStickyChange, setIsStickyChange] = useState(false);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const container = useMemo(() => tableHeadRef.current?.querySelector('#theadContainer'), [tableHeadRef.current]); // required tableHeadRef.current

  const handleTheadContainerScroll = useCallback(() => {
    window.requestAnimationFrame(() => {
      if (tableRef.current && container) {
        if (tableRef.current.scrollLeft !== container.scrollLeft) {
          tableRef.current.scrollLeft = container.scrollLeft;
        }
      }
    });
  }, [container]);

  const handleMainTableScroll = useCallback(() => {
    window.requestAnimationFrame(() => {
      if (container && tableRef.current) {
        container.scrollLeft = tableRef.current.scrollLeft;
        if (tableRef.current.scrollLeft !== container.scrollLeft) {
          tableRef.current.scrollLeft = container.scrollLeft;
        }
      }
    });
  }, [container]);

  useEffect(() => {
    if (container) {
      container.addEventListener('scroll', handleTheadContainerScroll);
    }

    return () => {
      if (container) {
        container.removeEventListener('scroll', handleTheadContainerScroll);
      }
    };
  }, [container, handleTheadContainerScroll]);

  useEffect(() => {
    const { current } = tableRef;

    if (current) {
      current.addEventListener('scroll', handleMainTableScroll);
    }

    return () => {
      if (current) {
        current.removeEventListener('scroll', handleMainTableScroll);
      }
    };
  }, [tableRef, container, handleMainTableScroll]);

  useEffect(() => {
    function onScroll() {
      const { documentElement } = document;
      const isScrollToBottom =
        documentElement.clientHeight + documentElement.scrollTop >= documentElement.scrollHeight - 10;

      if (isScrollToBottom) {
        dispatch(erknmAnalyticsProsecutorOrganizationGetDataNext());
      }
    }
    window.addEventListener('scroll', onScroll);

    return () => {
      window.removeEventListener('scroll', onScroll);
    };
  }, [dispatch]);

  useEffect(() => {
    setCurrentData(data);
  }, [data]);

  useUnmount(() => {
    setModifiedColumn(null);
  });

  const onOpenModal = (column: IErknmAnalyticsTableColumn) => () => setModifiedColumn(column);

  const onCloseModal = useCallback(() => setModifiedColumn(null), []);

  const onDisableAdditionalColumns = (id: string) => () => {
    dispatch(
      erknmAnalyticsProsecutorOrganizationSetTableColumns(
        tableColumns
          .filter(item => item.id !== 'name')
          .map(item => (item.mainId === id ? { ...item, isActive: !item.isActive } : item)),
      ),
    );
  };

  const getParams = (c: string, r: string, f: string | null) => {
    const params = new URLSearchParams();

    params.set('c', c);
    params.set('r', r);
    f && params.set('f', f);
    params.set('filter', Base64.encode(JSON.stringify(filter), true));

    return `${params}`;
  };

  const renderCell = (column: IErknmAnalyticsTableColumn, cellItem: IErknmAnalyticsIndex) => {
    const cell = cellItem.cs.find(item => item.c === column.id);

    if (cell?.v !== undefined) {
      switch (column.valueType) {
        case ErknmAnalyticsColumnValueTypeEnum.NUMBER: {
          if (cell.v > 0) {
            return (
              <Link
                to={`${ROUTE_ANALYTICS_PROSECTOR_KNM}?${getParams(column.id, cellItem.r, cell?.f ?? null)}`}
                className={styles.ExportButton}
              >
                {cell?.v}
              </Link>
            );
          }

          return <div className={styles.ExportButtonWithoutLink}>{cell.v}</div>;
        }
        case ErknmAnalyticsColumnValueTypeEnum.FLOAT: {
          if (cell.v > 0) {
            return (
              <Link
                to={`${ROUTE_ANALYTICS_PROSECTOR_KNM}?${getParams(column.id, cellItem.r, cell?.f ?? null)}`}
                className={styles.ExportButton}
              >
                {(cell?.v / 100).toFixed(2)}
              </Link>
            );
          }

          return <div className={styles.ExportButtonWithoutLink}>{cell.v}</div>;
        }
        case ErknmAnalyticsColumnValueTypeEnum.CURRENCY: {
          return <div className={styles.ExportButtonWithoutLink}>{formatCurrency(cell.v)}</div>;
        }
        default:
          return <div className={styles.ExportButtonWithoutLink}>Нет данных</div>;
      }
    }

    return null;
  };

  const renderFixedCell = (
    column: IErknmAnalyticsTableColumn,
    cellItem: IErknmAnalyticsIndex,
    isOddRow: boolean,
    isAdditional?: boolean,
  ) => {
    const dataAttributes = {
      ...(column.id === 'name' && { 'data-name': true }),
      ...(column.isMain && { 'data-main': true }),
      ...(isAdditional && { 'data-additional': true }),
      ...(isAdditional && isOddRow && { 'data-additional-row': true }),
      ...(column.isWarning && { 'data-warning': true }),
    };

    return (
      <td key={column.id + cellItem.r} className={styles.TbodyCell} {...dataAttributes}>
        <div className={styles.Export}>{column.id === 'name' ? cellItem.n : renderCell(column, cellItem)}</div>
      </td>
    );
  };

  return (
    <div className={styles.Container}>
      <StickyContainer>
        <table className={styles.TableHead} ref={tableHeadRef}>
          <Sticky disableCompensation disableHardwareAcceleration>
            {({ style, isSticky }) => {
              if (isSticky !== isStickyChange) {
                setIsStickyChange(isSticky);
              }

              return (
                <div
                  style={{ ...deleteObjectProperties(style, ['top', 'transform']), top: 70 }}
                  className={styles.TheadContainer}
                  id="theadContainer"
                >
                  <thead className={styles.Thead}>
                    <tr>
                      {tableColumns
                        .filter(item => !item.mainId)
                        .map(column => {
                          const additionalTableColumns = tableColumns.filter(
                            item => item.mainId === column.id && item.isActive,
                          );
                          const theadAttributes = {
                            ...(column.id === 'name' && { 'data-name': true }),
                            ...(column.isMain && { 'data-main': true }),
                            ...(column.isWarning && { 'data-warning': true }),
                          };
                          const disableButtonAttributes = {
                            ...(tableColumns.find(item => item.mainId === column.id && !item.isActive) && {
                              'data-active': true,
                            }),
                          };

                          return (
                            <Fragment key={column.id}>
                              <th key={column.id} className={styles.TheadCell} {...theadAttributes}>
                                {column.id !== 'name' && (
                                  <div className={styles.ButtonGroup}>
                                    <span className={styles.AddColumnButton} onClick={onOpenModal(column)} />
                                    {tableColumns.find(item => item.mainId === column.id) && (
                                      <span
                                        className={styles.DisableColumnsButton}
                                        {...disableButtonAttributes}
                                        onClick={onDisableAdditionalColumns(column.id)}
                                      />
                                    )}
                                  </div>
                                )}
                                {column.name}
                              </th>
                              {!!additionalTableColumns.length && (
                                <th
                                  className={styles.TheadAdditionalCell}
                                  colSpan={additionalTableColumns.length}
                                  key={`additional.${column.id}`}
                                >
                                  <div className={styles.AdditionalLabel}>Из Них:</div>
                                  <div className={styles.AdditionalContainer}>
                                    {additionalTableColumns.map(additionalItem => (
                                      <th key={additionalItem.id} className={styles.TheadAdditionalColSpan}>
                                        {additionalItem.name}
                                      </th>
                                    ))}
                                  </div>
                                </th>
                              )}
                            </Fragment>
                          );
                        })}
                    </tr>
                  </thead>
                </div>
              );
            }}
          </Sticky>
        </table>
        <table className={styles.Table} ref={tableRef}>
          <tbody>
            {currentData.map((item, index) => {
              return (
                <Accordion
                  key={item.r ?? index}
                  mainRow={
                    <>
                      {tableColumns
                        .filter(column => !column.mainId)
                        .map(column => (
                          <Fragment key={column.id}>
                            {renderFixedCell(column, item, true, item.rs.length > 0)}
                            {tableColumns
                              .filter(
                                additionalColumn => additionalColumn.mainId === column.id && additionalColumn.isActive,
                              )
                              .map(additionalColumn => (
                                <td key={`${additionalColumn.id}.additional`} className={styles.TbodyAdditionalCell}>
                                  <div className={styles.Export}>{renderCell(additionalColumn, item)}</div>
                                </td>
                              ))}
                          </Fragment>
                        ))}
                    </>
                  }
                >
                  {item.rs?.length ? (
                    <Fragment>
                      {item.rs.map((additionalRow, additionalIndex) => (
                        <tr key={additionalRow.r} className={styles.TableRow}>
                          {tableColumns
                            .filter(column => !column.mainId)
                            .map(column => (
                              <Fragment key={column.id}>
                                {renderFixedCell(column, additionalRow, additionalIndex % 2 === 1, true)}
                                {tableColumns
                                  .filter(
                                    additionalColumn =>
                                      additionalColumn.mainId === column.id && additionalColumn.isActive,
                                  )
                                  .map(additionalColumn => (
                                    <td
                                      key={`${additionalColumn.id}.additional`}
                                      className={styles.TbodyAdditionalCell}
                                    >
                                      <div className={styles.Export}>{renderCell(additionalColumn, additionalRow)}</div>
                                    </td>
                                  ))}
                              </Fragment>
                            ))}
                        </tr>
                      ))}
                    </Fragment>
                  ) : null}
                </Accordion>
              );
            })}
          </tbody>
        </table>
      </StickyContainer>
      <ErknmAnalyticsProsecutorOrganizationColumnModal modifiedColumn={modifiedColumn} onClose={onCloseModal} />
    </div>
  );
};

const mapStateToProps = (state: IRootState) => ({
  data: getErknmAnalyticsProsecutorOrganizationData(state),
  tableColumns: getErknmAnalyticsProsecutorOrganizationTableColumns(state),
  filter: getErknmAnalyticsProsecutorOrganizationFilter(state),
});

export const ErknmAnalyticsProsecutorOrganizationTable = connect(mapStateToProps)(
  ErknmAnalyticsProsecutorOrganizationTableWithoutStore,
);
